import dayjs from 'dayjs';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { RRule, WeekdayStr, Frequency, Options, Weekday } from 'rrule';

import { t } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';
import dateService from '@gem/services/date-service';

import { getHumanizeRRule, getUnitLabel } from 'APP/utils/calendar/getHumanizeRRule';

import { DAYS_MAP, EndEnum, EndMap } from './CustomRepeatPopup.constants';
import { IFormData } from './CustomRepeatPopup.types';

export interface ICustomRepeatPopupParams {
  onSubmit(values: { key: string; title: string }): void;
  startDate: Date;
  customRepeatValue: string | undefined;
}

interface ICustomRepeatPopupPresenter {
  initialValues: IFormData;
  isShowLastDayField: boolean;
  getFrequencyItems(interval: number): { key: string; title: string }[];
  onChangeFrequency(value: string): void;
  endItems: { key: string; title: string }[];
  weekdays: Array<{ value: WeekdayStr; title: string }>;
  onSubmit(data: IFormData): Promise<void>;
  checkLastDayOfMonth(date: Date): boolean;
  setShowLastDayField(isLastDay: boolean): void;
  onClose(): void;
  handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void;
  handlePaste(e: ClipboardEvent<HTMLInputElement>): void;
}

interface ICustomRepeatPopupPresenterReturnType {
  presenter: ICustomRepeatPopupPresenter;
  form: UseFormReturn<IFormData>;
}

export function useCustomRepeatPopupPresenter({
  popupInstance,
  onClose,
  params,
}: ICommonPopupsProps<ICustomRepeatPopupParams>): ICustomRepeatPopupPresenterReturnType {
  const presenter = useLocalObservable<ICustomRepeatPopupPresenter>(() => ({
    initialValues: {
      interval: 1,
      count: '10',
      until: dayjs().add(1, 'month').toDate(),
      byweekday: [
        {
          value: params?.startDate ? DAYS_MAP[(params.startDate.getDay() + 6) % 7] : 'MO',
        },
      ],
      frequency: `${Frequency.DAILY}`,
      end: EndEnum.NotEnd,
      isSameDay: false,
      isLastDay: false,
      isFirstMonday: true,
    },
    isShowLastDayField: false,

    async onSubmit(values: IFormData): Promise<void> {
      let rrule: RRule = new RRule({});
      const until = dayjs(values.until).endOf('day').toDate();

      if (+values.frequency === Frequency.DAILY) {
        let rule: Partial<Options> = {
          freq: RRule.DAILY,
          interval: values.interval,
          dtstart: params?.startDate,
        };
        if (values.end === EndEnum.NumberOccurrences) {
          rule = {
            ...rule,
            count: +values.count,
          };
        }
        if (values.end === EndEnum.OnDate) {
          rule = {
            ...rule,
            until,
          };
        }
        rrule = new RRule(rule);
      }
      if (+values.frequency === Frequency.WEEKLY) {
        let rule: Partial<Options> = {
          freq: +values.frequency,
          interval: values.interval,
          byweekday: values.byweekday
            .map((day) => Weekday.fromStr(day.value))
            .sort((a, b) => a.weekday - b.weekday),
          dtstart: params?.startDate,
        };

        if (values.end === EndEnum.NumberOccurrences) {
          rule = {
            ...rule,
            count: +values.count,
          };
        }
        if (values.end === EndEnum.OnDate) {
          rule = {
            ...rule,
            until,
          };
        }
        rrule = new RRule(rule);
      }
      if (+values.frequency === Frequency.MONTHLY) {
        let rule: Partial<Options> = {
          freq: RRule.MONTHLY,
          interval: values.interval,
          dtstart: params?.startDate,
        };
        if (values.isFirstMonday) {
          rule = {
            ...rule,
            byweekday: RRule.MO,
            bysetpos: 1,
          };
        }
        if (values.isSameDay) {
          rule = {
            ...rule,
            bymonthday: params?.startDate.getDate(),
          };
        }
        if (values.isLastDay) {
          rule = {
            ...rule,
            bymonthday: -1,
          };
        }
        if (values.end === EndEnum.NumberOccurrences) {
          rule = {
            ...rule,
            count: +values.count,
          };
        }
        if (values.end === EndEnum.OnDate) {
          rule = {
            ...rule,
            until,
          };
        }
        rrule = new RRule(rule);
      }
      if (+values.frequency === Frequency.YEARLY) {
        let rule: Partial<Options> = {
          freq: RRule.YEARLY,
          interval: values.interval,
          dtstart: params?.startDate,
        };
        if (values.end === EndEnum.NumberOccurrences) {
          rule = {
            ...rule,
            count: +values.count,
          };
        }
        if (values.end === EndEnum.OnDate) {
          rule = {
            ...rule,
            until,
          };
        }
        rrule = new RRule(rule);
      }

      const value =
        rrule
          .toString()
          .split('\n')
          .find((s) => s.startsWith('RRULE:'))
          ?.replace('FREQ=', '')
          .replace('RRULE:', '') || '';

      params?.onSubmit({
        key: value,
        title: getHumanizeRRule(value),
      });

      popupInstance.close();
    },

    handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
      const invalidChars = ['-', '+', 'e', 'E', '.', ','];
      if (invalidChars.includes(e.key)) {
        e.preventDefault();
      }
    },

    handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
      const paste = e.clipboardData.getData('text');
      if (/[^0-9]/.test(paste)) {
        e.preventDefault();
      }
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    setShowLastDayField(isLastDay: boolean): void {
      presenter.isShowLastDayField = isLastDay;
    },

    checkLastDayOfMonth(date: Date): boolean {
      const tomorrow = new Date(date);
      tomorrow.setDate(date.getDate() + 1);

      return tomorrow.getMonth() !== date.getMonth();
    },

    get weekdays(): Array<{ value: WeekdayStr; title: string }> {
      const baseDate = new Date(2024, 0, 1);

      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + i);

        const dayName = dateService.format(date, { weekday: 'short' }) || '';

        return {
          value: DAYS_MAP[i],
          title: dayName.charAt(0).toUpperCase() + dayName.slice(1),
        };
      });
    },

    getFrequencyItems(interval: number): { key: string; title: string }[] {
      return Object.entries({
        [`${Frequency.DAILY}`]: getUnitLabel(interval, 'day'),
        [`${Frequency.WEEKLY}`]: getUnitLabel(interval, 'week'),
        [`${Frequency.MONTHLY}`]: getUnitLabel(interval, 'month'),
        [`${Frequency.YEARLY}`]: getUnitLabel(interval, 'year'),
      })
        .map(([key, value]) => ({
          key,
          title: value,
        }))
        .sort((a, b) => +b.key - +a.key);
    },

    onChangeFrequency(value: string): void {
      if (+value === Frequency.DAILY) {
        form.setValue('until', dayjs().add(1, 'month').toDate());
      }

      if (+value === Frequency.WEEKLY) {
        form.setValue('until', dayjs().add(3, 'month').toDate());
      }

      if (+value === Frequency.MONTHLY) {
        form.setValue('until', dayjs().add(1, 'year').toDate());
      }

      if (+value === Frequency.YEARLY) {
        form.setValue('until', dayjs().add(5, 'year').toDate());
      }
    },

    get endItems(): { key: string; title: string }[] {
      return Object.entries(EndMap).map(([key, value]) => ({
        key,
        title: t(value),
      }));
    },
  }));

  const form = useForm<IFormData>({
    mode: 'onChange',
    defaultValues: presenter.initialValues,
  });

  useEffect(() => {
    if (!params?.customRepeatValue) {
      return;
    }

    const rule = RRule.fromString(`RRULE:FREQ=${params?.customRepeatValue}`);

    form.setValue('interval', rule.options.interval);
    form.setValue('frequency', `${rule.options.freq}`);

    if (rule.options.count) {
      form.setValue('count', `${rule.options.count}`);
      form.setValue('end', EndEnum.NumberOccurrences);
    }

    if (rule.options.until) {
      form.setValue('until', rule.options.until);
      form.setValue('end', EndEnum.OnDate);
    }

    if (rule.options.freq === Frequency.WEEKLY && rule.options.byweekday) {
      form.setValue(
        'byweekday',
        rule.options.byweekday.map((day) => ({
          value: new Weekday(day).toString() as WeekdayStr,
        }))
      );
    }

    if (
      rule.options.freq === Frequency.MONTHLY &&
      rule.options.byweekday?.includes(RRule.MO.weekday) &&
      rule.options.bysetpos.length
    ) {
      form.setValue('isFirstMonday', true);
      form.setValue('isSameDay', false);
    }

    if (rule.options.freq === Frequency.MONTHLY && rule.options.bymonthday.length) {
      const [monthDayNumber] = rule.options.bymonthday;
      if (monthDayNumber === 0) {
        form.setValue('isSameDay', true);
        form.setValue('isFirstMonday', false);
      }
    }

    if (rule.options.freq === Frequency.MONTHLY && rule.options.bynmonthday.length) {
      const [monthDayNumber] = rule.options.bynmonthday;
      if (monthDayNumber === -1) {
        form.setValue('isLastDay', true);
        form.setValue('isFirstMonday', false);
      }
    }
  }, [params?.customRepeatValue]);

  useEffect(() => {
    if (!params?.startDate) {
      return;
    }

    const isLastDay = presenter.checkLastDayOfMonth(params.startDate);

    presenter.setShowLastDayField(isLastDay);
  }, [params?.startDate]);

  return { presenter, form };
}
